/**
 * Check LOR Progress Button
 *
 * A button that shows workflow progress during LOR AI review.
 * Displays a progress bar and step description on hover.
 */
import { Sparkles } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Workflow status values (matches backend LorReviewStatus enum)
type LorReviewStatus = 'REVIEWING' | 'COMPLETED' | 'FAILED';

interface WorkflowStep {
  status: LorReviewStatus | 'PENDING';
  label: string;
  description: string;
  progress: number;
}

const WORKFLOW_STEPS: WorkflowStep[] = [
  {
    status: 'PENDING',
    label: 'Starting',
    description: 'Starting review...',
    progress: 10,
  },
  {
    status: 'REVIEWING',
    label: 'AI Review',
    description: 'FinchGPT is reviewing your LOR fields...',
    progress: 50,
  },
  {
    status: 'COMPLETED',
    label: 'Complete',
    description: 'Review complete - check the chat for results!',
    progress: 100,
  },
  {
    status: 'FAILED',
    label: 'Failed',
    description: 'Something went wrong. Please try again.',
    progress: 0,
  },
];

function getStepFromStatus(status: string | null): WorkflowStep {
  if (!status) {
    return WORKFLOW_STEPS[0]; // PENDING
  }

  const normalizedStatus = status.toUpperCase();
  const step = WORKFLOW_STEPS.find((s) => s.status === normalizedStatus);

  if (step) {
    return step;
  }

  // Try to match by description keywords for custom status descriptions
  const statusLower = status.toLowerCase();
  if (statusLower.includes('review') || statusLower.includes('ai')) {
    return WORKFLOW_STEPS[1]; // REVIEWING
  }
  if (statusLower.includes('complete') || statusLower.includes('success')) {
    return WORKFLOW_STEPS[2]; // COMPLETED
  }
  if (statusLower.includes('fail') || statusLower.includes('error')) {
    return WORKFLOW_STEPS[3]; // FAILED
  }

  return WORKFLOW_STEPS[0]; // Default to PENDING
}

interface CheckLorProgressButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isProcessing: boolean;
  currentStatus: string | null;
  className?: string;
  /** Custom label for the idle button state. Defaults to "Check LoR". */
  label?: string;
}

export const CheckLorProgressButton: React.FC<CheckLorProgressButtonProps> = ({
  onClick,
  disabled = false,
  isProcessing,
  currentStatus,
  className,
  label = 'Check LoR',
}) => {
  const step = getStepFromStatus(currentStatus);
  const isFailed = step.status === 'FAILED';

  if (!isProcessing) {
    return (
      <Button
        variant="outline"
        className={cn('w-full', className)}
        onClick={onClick}
        disabled={disabled}
      >
        <Sparkles className="mr-2 h-4 w-4" />
        {label}
      </Button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'relative w-full overflow-hidden transition-all',
            isFailed && 'border-red-300 text-red-600',
            className,
          )}
          disabled
        >
          {/* Progress bar background */}
          <div
            className={cn(
              'absolute inset-0 transition-all duration-500 ease-out',
              isFailed ? 'bg-red-100' : 'bg-emerald-100',
            )}
            style={{
              width: `${step.progress}%`,
            }}
          />

          {/* Button content */}
          <span className="relative z-10 flex items-center gap-2">
            {/* Animated spinner */}
            {!isFailed && (
              <svg
                className="h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            <span className="font-medium">{step.label}</span>
            <span className="text-xs opacity-70">({step.progress}%)</span>
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <div className="space-y-2">
          <p className="font-medium">{step.label}</p>
          <p className="text-xs opacity-90">{step.description}</p>
          <div className="flex items-center gap-2 pt-1">
            <div className="h-1.5 flex-1 rounded-full bg-gray-600">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  isFailed ? 'bg-red-400' : 'bg-emerald-400',
                )}
                style={{ width: `${step.progress}%` }}
              />
            </div>
            <span className="text-[10px] tabular-nums">{step.progress}%</span>
          </div>
          {/* Step indicators */}
          <div className="flex justify-between pt-1">
            {WORKFLOW_STEPS.slice(0, -1).map((s) => (
              <div
                key={s.status}
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-colors',
                  s.progress <= step.progress
                    ? isFailed
                      ? 'bg-red-400'
                      : 'bg-emerald-400'
                    : 'bg-gray-500',
                )}
                title={s.label}
              />
            ))}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
